<template>
  <div class="downloads-page page-body">
    <div class="dl-group-hero">
      <CategoryBanner
        full
        :tone="downloadPageHero.tone"
        :icon="downloadPageHero.icon"
        :eyebrow="downloadPageHero.eyebrow"
        :title="downloadPageHero.title"
        :desc="downloadPageHero.desc"
        :image="downloadPageHero.image"
        :photo-frame="downloadPageHero.photoFrame"
      />
    </div>

    <div class="dl-subnav">
      <div class="dl-subnav__inner">
        <nav class="dl-breadcrumb" aria-label="面包屑">
          <a href="#" class="dl-breadcrumb__link" @click.prevent="goHome">主页</a>
          <span class="dl-breadcrumb__sep">/</span>
          <span class="dl-breadcrumb__current">下载中心</span>
        </nav>
      </div>
    </div>

    <div class="dl-content">
      <section v-if="featured" class="featured-download">
        <article class="featured-card premium-card" data-reveal>
          <div class="featured-card__cover">
            <img :src="featured.cover" :alt="featured.title" />
          </div>
          <div class="featured-card__body">
            <span class="featured-card__badge">推荐下载</span>
            <h2>{{ featured.title }}</h2>
            <p>{{ featured.desc }}</p>
            <div class="featured-card__meta">
              <span>{{ featured.format }}</span>
              <span>{{ featured.size }}</span>
            </div>
            <a
              class="btn btn--primary"
              :href="featured.url"
              target="_blank"
              rel="noopener noreferrer"
            >
              立即下载
            </a>
          </div>
        </article>
      </section>

      <section class="download-section">
        <div class="filter-row" data-reveal>
          <button
            v-for="cat in tabs"
            :key="cat.id"
            type="button"
            class="filter-chip"
            :class="{ 'filter-chip--active': activeCategory === cat.id }"
            @click="activeCategory = cat.id"
          >
            {{ cat.label }}
          </button>
        </div>

        <div class="download-grid">
          <article
            v-for="(item, idx) in filteredItems"
            :key="`${activeCategory}-${item.id}`"
            class="download-card"
            data-reveal
            :data-reveal-delay="String(idx % 4 + 1)"
          >
            <div class="download-card__cover">
              <img :src="item.cover" :alt="item.title" />
              <span v-if="item.isVideo" class="download-card__play">▶</span>
            </div>
            <div class="download-card__body">
              <h3>{{ item.title }}</h3>
              <p>{{ item.desc }}</p>
              <div class="download-card__footer">
                <span class="download-card__format">{{ item.format }} · {{ item.size }}</span>
                <a
                  v-if="!item.isVideo"
                  class="download-card__btn"
                  :href="item.url"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  下载
                </a>
                <button v-else type="button" class="download-card__btn" @click="showVideoTip">
                  播放
                </button>
              </div>
            </div>
          </article>
        </div>
        <p v-if="!filteredItems.length" class="download-empty">该分类暂无资料</p>
      </section>
    </div>
  </div>
</template>

<script setup>
import { computed, nextTick, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { ElMessage } from 'element-plus'
import CategoryBanner from '../components/CategoryBanner.vue'
import { downloadCategories, downloadItems, downloadPageHero } from '../data/downloadsCatalog'
import { useScrollReveal } from '../composables/useScrollReveal'

const router = useRouter()
const { refreshReveal } = useScrollReveal()

const tabs = [{ id: 'all', label: '全部' }, ...downloadCategories]
const activeCategory = ref('all')

const featured = computed(() => downloadItems.find((item) => item.featured))

const filteredItems = computed(() => {
  if (activeCategory.value === 'all') {
    // 「全部」列表不重复展示顶部推荐项
    return downloadItems.filter((item) => !item.featured)
  }
  return downloadItems.filter((item) => item.category === activeCategory.value)
})

watch(filteredItems, async () => {
  await nextTick()
  refreshReveal()
})

function goHome() {
  router.push({ name: 'home' })
}

function showVideoTip() {
  ElMessage.info('演示视频即将上线，敬请期待')
}
</script>

<style scoped>
.downloads-page {
  min-height: calc(100vh - 72px);
  width: 100%;
  overflow-x: hidden;
}

.dl-group-hero {
  width: 100%;
}

.dl-subnav {
  background: transparent;
  border: none;
}

.dl-subnav__inner {
  max-width: min(1400px, 100%);
  margin: 0 auto;
  padding: 14px clamp(16px, 4vw, 72px);
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-sizing: border-box;
}

.dl-breadcrumb {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
}

.dl-breadcrumb__link {
  color: #888;
  text-decoration: none;
  transition: color 0.2s;
}

.dl-breadcrumb__link:hover {
  color: var(--brand-primary);
}

.dl-breadcrumb__sep {
  color: #ccc;
}

.dl-breadcrumb__current {
  color: #333;
  font-weight: 500;
}

.dl-content {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 40px 64px;
}

.featured-download {
  padding: 0 0 32px;
}

.featured-card {
  display: grid;
  grid-template-columns: 280px 1fr;
  gap: 0;
}

.featured-card__cover {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 220px;
  padding: 16px;
  background: linear-gradient(180deg, #f8fbff 0%, #eef4fb 100%);
}

.featured-card__cover img {
  width: 100%;
  height: 100%;
  max-height: 220px;
  object-fit: contain;
  object-position: center center;
}

.featured-card__body {
  padding: 32px 36px;
}

.featured-card__badge {
  display: inline-block;
  padding: 4px 12px;
  margin-bottom: 12px;
  background: rgba(23, 107, 137, 0.08);
  color: var(--brand-primary);
  font-size: 12px;
  font-weight: 700;
  border-radius: var(--radius-pill);
}

.featured-card h2 {
  margin: 0 0 10px;
  font-size: 24px;
}

.featured-card p {
  margin: 0 0 16px;
  color: var(--text-secondary);
  line-height: 1.6;
}

.featured-card__meta {
  display: flex;
  gap: 16px;
  margin-bottom: 20px;
  font-size: 13px;
  color: var(--text-muted);
}

.download-section {
  padding-bottom: 0;
}

.filter-row {
  margin-bottom: 32px;
  justify-content: center;
}

.download-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
}

.download-empty {
  margin: 48px 0 0;
  text-align: center;
  font-size: 14px;
  color: var(--text-muted, #999);
}

.download-card__cover {
  position: relative;
  aspect-ratio: 4/3;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 14px;
  background: linear-gradient(180deg, #f8fbff 0%, #eef4fb 100%);
}

.download-card__cover img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  object-position: center center;
}

.download-card__play {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.25);
  color: #fff;
  font-size: 28px;
}

.download-card__body {
  padding: 20px;
}

.download-card h3 {
  margin: 0 0 8px;
  font-size: 16px;
}

.download-card p {
  margin: 0 0 16px;
  font-size: 13px;
  color: var(--text-secondary);
  line-height: 1.55;
}

.download-card__footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.download-card__format {
  font-size: 12px;
  color: var(--text-muted);
}

.download-card__btn {
  padding: 8px 18px;
  border: none;
  border-radius: var(--radius-pill);
  background: var(--gradient-brand);
  color: #fff;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  text-decoration: none;
  box-shadow: var(--shadow-brand);
  transition: transform 0.2s;
}

.download-card__btn:hover {
  transform: translateY(-1px);
}

@media (max-width: 960px) {
  .dl-content {
    padding-left: 16px;
    padding-right: 16px;
  }

  .featured-card {
    grid-template-columns: 1fr;
  }

  .download-grid {
    grid-template-columns: 1fr;
  }
}
</style>
